/**
 * Natural Earth specific data handling — filtering, attribute extraction
 */
import { iso3ToIso2 } from '../utils/iso-codes.js';
import { SUBREGION_COUNTRIES } from '../utils/geo-utils.js';

// Fallback: country NAME → ISO3 for Natural Earth features with ISO_A3 = '-99'
const _NAME_TO_ISO3 = {
  'norway': 'NOR', 'france': 'FRA', 'kosovo': 'KOS',
  'n. cyprus': 'CYP', 'somaliland': 'SOM',
};

/**
 * Get the ISO3 code from a feature's properties, checking standard then legacy names.
 *
 * Natural Earth encodes several sovereign states (France, Norway, Kosovo, …) with
 * ISO_A3 === '-99' while carrying the real code in ADM0_A3. Fall back to ADM0_A3
 * whenever the primary field is missing or the '-99' sentinel, so country lookups
 * (filterByCountry, filterByContinent) don't silently drop those states.
 */
function _getIso3(props) {
  const primary = props.iso3 || props.ISO_A3 || props.iso_a3 || '';
  if (primary && primary !== '-99') return primary;
  return props.ADM0_A3 || props.adm0_a3 || primary || '';
}

/**
 * Get the ISO2 code from a feature's properties.
 */
function _getIso2(props) {
  return props.iso2 || props.iso_a2 || props.ISO_A2 || '';
}

/**
 * Get the display name from a feature's properties.
 */
function _getName(props) {
  return props.name_en || props.NAME || props.name || '';
}

/**
 * Filter features by country ISO code
 */
export function filterByCountry(geojson, isoCode) {
  if (!geojson?.features || !isoCode) return geojson;

  const iso = isoCode.toUpperCase();
  const iso2 = iso3ToIso2(iso) || iso.substring(0, 2);
  const filtered = geojson.features.filter(f => {
    const props = f.properties;
    return (_getIso3(props) === iso || _getIso2(props) === iso2 ||
            props.admin === getCountryName(iso) || props.ADM0NAME === getCountryName(iso) ||
            props.country_name === getCountryName(iso) ||
            props.iso_3166_2?.startsWith(iso2));
  });

  return { type: 'FeatureCollection', features: filtered };
}

/**
 * Filter features by continent or named subregion.
 * Named subregions (e.g., "Central America") use explicit ISO code lists
 * to avoid UN subregion classification issues (e.g., Mexico being grouped with Central America).
 */
export function filterByContinent(geojson, continent) {
  if (!geojson?.features || !continent) return geojson;

  // Check for named subregion with explicit country list
  const isoSet = SUBREGION_COUNTRIES[continent];
  if (isoSet) {
    const filtered = geojson.features.filter(f => {
      const props = f.properties || {};
      let iso = _getIso3(props).toUpperCase();
      // Some Natural Earth features have ISO_A3 = '-99' (e.g., Norway, France, Kosovo).
      // Fall back to name-based ISO lookup for these.
      if (iso === '-99' || !iso) {
        iso = _NAME_TO_ISO3[_getName(props).toLowerCase()] || '';
      }
      return isoSet.has(iso);
    });
    return { type: 'FeatureCollection', features: filtered };
  }

  const filtered = geojson.features.filter(f => {
    const props = f.properties;
    const cont = props.continent || props.CONTINENT || '';
    return cont.toLowerCase() === continent.toLowerCase();
  });

  return { type: 'FeatureCollection', features: filtered };
}

/**
 * Filter features by subregion
 */
export function filterBySubregion(geojson, subregion) {
  if (!geojson?.features || !subregion) return geojson;

  const sub = subregion.toLowerCase();
  const filtered = geojson.features.filter(f => {
    const props = f.properties;
    const sr = props.subregion || props.SUBREGION || '';
    return sr.toLowerCase() === sub;
  });

  return { type: 'FeatureCollection', features: filtered };
}

/**
 * Filter features by attribute value
 */
export function filterByAttribute(geojson, attribute, operator, value) {
  if (!geojson?.features) return geojson;

  const filtered = geojson.features.filter(f => {
    const v = f.properties?.[attribute];
    if (v == null) return false;

    switch (operator) {
      case '>': return v > value;
      case '<': return v < value;
      case '>=': return v >= value;
      case '<=': return v <= value;
      // eslint-disable-next-line eqeqeq -- intentional loose equality: '==' filter operator compares a user-supplied value (often string) against a feature property (often number)
      case '==': return v == value;
      // eslint-disable-next-line eqeqeq -- intentional loose equality: '!=' filter operator, same cross-type rationale as '=='
      case '!=': return v != value;
      case 'contains': return String(v).toLowerCase().includes(String(value).toLowerCase());
      default: return true;
    }
  });

  return { type: 'FeatureCollection', features: filtered };
}

/**
 * Rank features by an attribute, returning top N or bottom N
 */
export function rankFeatures(geojson, attribute, direction = 'top', count = 10) {
  if (!geojson?.features) return geojson;

  const withValues = geojson.features.filter(f => {
    const v = f.properties?.[attribute];
    return v != null && !isNaN(v);
  });

  withValues.sort((a, b) => {
    const aVal = a.properties[attribute];
    const bVal = b.properties[attribute];
    return direction === 'top' ? bVal - aVal : aVal - bVal;
  });

  return {
    type: 'FeatureCollection',
    features: withValues.slice(0, count)
  };
}

/**
 * Filter cities by country
 */
export function filterCitiesByCountry(geojson, countryName) {
  if (!geojson?.features || !countryName) return geojson;

  const name = countryName.toLowerCase();
  const filtered = geojson.features.filter(f => {
    const props = f.properties;
    const cn = (props.country_name || props.ADM0NAME || '').toLowerCase();
    return cn === name || props.SOV0NAME?.toLowerCase() === name;
  });

  return { type: 'FeatureCollection', features: filtered };
}

/**
 * Filter cities by continent (requires countries dataset for lookup)
 */
export function filterCitiesByContinent(citiesGeojson, countriesGeojson, continent) {
  if (!citiesGeojson?.features || !countriesGeojson?.features) return citiesGeojson;

  // Build a set of country names in the continent
  const countriesInContinent = new Set();
  for (const f of countriesGeojson.features) {
    const cont = f.properties.continent || f.properties.CONTINENT || '';
    if (cont.toLowerCase() === continent.toLowerCase()) {
      countriesInContinent.add(_getName(f.properties).toLowerCase());
    }
  }

  const filtered = citiesGeojson.features.filter(f => {
    const cn = (f.properties.country_name || f.properties.ADM0NAME || '').toLowerCase();
    return countriesInContinent.has(cn);
  });

  return { type: 'FeatureCollection', features: filtered };
}

// Mapping of ISO3 codes to country names (built from data)
let isoToNameMap = null;

export function setIsoToNameMap(countriesData) {
  isoToNameMap = new Map();
  if (countriesData?.features) {
    for (const f of countriesData.features) {
      const iso = _getIso3(f.properties);
      const name = _getName(f.properties);
      if (iso && name) isoToNameMap.set(iso, name);
    }
  }
}

export function getCountryName(isoCode) {
  return isoToNameMap?.get(isoCode) || isoCode;
}
